import React, { useMemo, useState, useEffect, useCallback, useRef, Fragment } from "react";
import { Button } from "components/ui";
import { Input } from "components/ui/Form";
import {
  PlusIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
  ClockIcon,
  UserIcon,
  Squares2X2Icon,
  ListBulletIcon,
  FunnelIcon,
  EllipsisHorizontalIcon,
  PaperClipIcon,
} from "@heroicons/react/24/outline";
import clsx from "clsx";
import { formatDisplayDate } from "utils/dateUtils";
import CustomTable from "custom-components/shared/CustomTable";
import TableActionMenu from "custom-components/shared/TableActionMenu";
import { MultiSelectCheckboxDropdown } from "custom-components/shared/MultiSelectCheckboxDropdown";
import { TextCell } from "custom-components/shared/CustomTable/rows";
import { getDynamicFieldValue } from "utils/dynamicFields";
import { SalesPageLayoutContext } from "app/layouts/SalesLayout/SalesPageLayout";
import { getAttachmentPreviewUrl, DocumentUploadFeature } from "services/documentUploadService";

function DynamicCellRenderer({ row, fieldName, onPreviewDocument }) {
  const rawVal = getDynamicFieldValue(row, fieldName);
  if (
    rawVal === null ||
    rawVal === undefined ||
    String(rawVal).trim() === "" ||
    String(rawVal).trim() === "-" ||
    String(rawVal).trim() === "[]" ||
    (Array.isArray(rawVal) && rawVal.length === 0)
  ) {
    return <span className="text-gray-400 dark:text-dark-500">—</span>;
  }

  // Check if rawVal is a JSON array of attachments
  let parsedFiles = null;
  let isJsonArray = false;
  if (typeof rawVal === "string" && rawVal.trim().startsWith("[")) {
    try {
      const arr = JSON.parse(rawVal);
      if (Array.isArray(arr)) {
        isJsonArray = true;
        if (arr.length > 0 && (arr[0].filePath || arr[0].fileName)) {
          parsedFiles = arr;
        }
      }
    } catch (e) {}
  } else if (Array.isArray(rawVal)) {
    isJsonArray = true;
    if (rawVal.length > 0 && (rawVal[0].filePath || rawVal[0].fileName)) {
      parsedFiles = rawVal;
    }
  }

  if (parsedFiles) {
    const handlePreview = async (e, file) => {
      e.stopPropagation();
      if (!file.filePath) return;
      if (onPreviewDocument) {
        onPreviewDocument({
          ...file,
          name: file.fileName || file.name || file.filePath?.split("/").pop() || "Attachment",
          fileName: file.fileName || file.name || file.filePath?.split("/").pop() || "Attachment",
          FileName: file.fileName || file.name || file.filePath?.split("/").pop() || "Attachment",
          fileSizeBytes: file.fileSizeKB ? (file.fileSizeKB * 1024) : (file.size || file.FileSize || 0),
          feature: DocumentUploadFeature.CLIENT_NOTE_ATTACHMENT
        });
      } else {
        try {
          const res = await getAttachmentPreviewUrl(file.filePath, null, DocumentUploadFeature.CLIENT_NOTE_ATTACHMENT);
          if (res.success && res.url) {
            window.open(res.url, "_blank");
          }
        } catch (err) {
          console.error("Failed to preview file:", err);
        }
      }
    };

    return (
      <div className="flex flex-wrap gap-1.5 py-0.5" onClick={(e) => e.stopPropagation()}>
        {parsedFiles.map((file, idx) => {
          const fName = file.fileName || file.name || file.filePath?.split("/").pop() || "Attachment";
          return (
            <button
              key={idx}
              type="button"
              onClick={(e) => handlePreview(e, file)}
              className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-white dark:bg-dark-800 border border-slate-200 dark:border-dark-700 hover:border-indigo-500 dark:hover:border-indigo-400 rounded-lg text-xs text-slate-800 dark:text-slate-200 font-medium transition-all shadow-2xs group cursor-pointer"
              title={`Click to preview/download ${fName}`}
            >
              <PaperClipIcon className="h-3.5 w-3.5 text-rose-500 shrink-0" />
              <span className="truncate max-w-[130px]">{fName}</span>
              <EyeIcon className="h-3.5 w-3.5 text-slate-400 group-hover:text-indigo-500 ml-0.5 shrink-0 transition-colors" />
            </button>
          );
        })}
      </div>
    );
  }

  if (isJsonArray) {
    return <span className="text-gray-400 dark:text-dark-500">—</span>;
  }

  return <TextCell getValue={() => rawVal} />;
}

const stripHtml = (htmlString) => {
  if (!htmlString) return "";
  let str = String(htmlString);
  if (typeof window !== "undefined" && typeof DOMParser !== "undefined") {
    try {
      const doc = new DOMParser().parseFromString(str, "text/html");
      let text = doc.body.textContent || "";
      if (/<[a-z][\s\S]*>/i.test(text) || /&[a-z0-9]+;/i.test(text)) {
        const doc2 = new DOMParser().parseFromString(text, "text/html");
        text = doc2.body.textContent || text;
      }
      return text.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
    } catch (e) {}
  }
  str = str.replace(/&lt;/gi, "<").replace(/&gt;/gi, ">");
  str = str.replace(/<[^>]*>/g, " ");
  return str
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'")
    .replace(/\s+/g, " ")
    .trim();
};

export default function ClientNotesList({
  data,
  totalRecords = 0,
  currentPage = 1,
  setCurrentPage,
  pageSize = 100,
  setPageSize,
  search = "",
  setSearch,
  selectedNoteTypes = [],
  setSelectedNoteTypes,
  noteTypeOptions = [],
  noteTypeLoading = false,
  loading,
  onView,
  onEdit,
  onDelete,
  onAddClick,
  getWorkStatusColor,
  getCostStatusColor,
  isMobile,
  onStatusClick,
  users = [],
  workStatuses = [],
  costStatuses = [],
  availableColumns = [],
  visibility,
  setVisibility,
  canEdit = true,
  onPreviewDocument,
}) {
  const [sortConfig, setSortConfig] = useState({
    key: "CreatedAt",
    direction: "desc",
  });
  const [viewMode, setViewMode] = useState("table");

  const [portalElement, setPortalElement] = useState(null);
  const portalRef = useCallback((node) => {
    if (node !== null) {
      setPortalElement(node);
    }
  }, []);

  // Clear client notes table settings from local storage on first load in this tab session
  useEffect(() => {
    try {
      const isInitialized = sessionStorage.getItem("client-notes-table-initialized-v4");
      if (!isInitialized) {
        localStorage.removeItem("client-notes-table-v4-settings");
        localStorage.removeItem("client-notes-table-v4-visibility");
        localStorage.removeItem("client-notes-table-v4-pinning");
        localStorage.removeItem("client-notes-table-v4-column-order");
        sessionStorage.setItem("client-notes-table-initialized-v4", "true");
      }
    } catch (e) {
      console.error("Failed to reset client notes storage:", e);
    }
  }, []);

  const resolveUserName = useCallback(
    (userIdOrName, fallback = "SunnyHomes Admin") => {
      if (!userIdOrName) return fallback;
      const isGuid = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/.test(
        String(userIdOrName)
      );
      if (isGuid) {
        const foundUser = users.find(
          (u) =>
            String(u.userId || u.UserId || u.id).toLowerCase() ===
            String(userIdOrName).toLowerCase()
        );
        return foundUser ? foundUser.fullName || foundUser.FullName : fallback;
      }
      return userIdOrName;
    },
    [users]
  );

  const itemsPerPage = pageSize;

  const renderActionsMenu = (item) => {
    const isAutoGenerated = Boolean(item.IsAutoGenerated);
    return (
      <TableActionMenu
        onView={() => onView(item)}
        onEdit={() => onEdit(item)}
        onDelete={() => onDelete(item.Id)}
        canEdit={canEdit}
        canDelete={canEdit}
        editDisabled={isAutoGenerated}
        editDisabledTooltip="Automatically generated when selections were locked — view only"
        deleteDisabled={isAutoGenerated}
        deleteDisabledTooltip="Automatically generated when selections were locked — view only"
      />
    );
  };

  // Handle Sort config toggling
  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  // Local sorting only (filtering and paging are done by the server)
  const paginatedData = useMemo(() => {
    let result = [...data];

    // Sorting
    if (sortConfig.key) {
      result.sort((a, b) => {
        let aVal = a[sortConfig.key];
        let bVal = b[sortConfig.key];

        // Handle case insensitive sorting for strings
        if (typeof aVal === "string") aVal = aVal.toLowerCase();
        if (typeof bVal === "string") bVal = bVal.toLowerCase();

        if (aVal === null || aVal === undefined) return 1;
        if (bVal === null || bVal === undefined) return -1;

        if (aVal < bVal) {
          return sortConfig.direction === "asc" ? -1 : 1;
        }
        if (aVal > bVal) {
          return sortConfig.direction === "asc" ? 1 : -1;
        }
        return 0;
      });
    }

    return result;
  }, [data, sortConfig]);

  const totalPages = Math.ceil(totalRecords / itemsPerPage);

  const columns = useMemo(() => {
    const hasData = (checkFn) => {
      if (!data || data.length === 0) return false;
      return data.some(checkFn);
    };

    const allStaticCols = [
      {
        key: "NoteType",
        header: "Type",
        hasValue: (item) => {
          const val = item.NoteType;
          return val && String(val).trim() !== "" && String(val).trim() !== "-";
        },
        render: (row) => {
          const item = row.original;
          return item.NoteType ? (
            <span className="inline-block rounded-md border border-indigo-100/80 bg-indigo-50 px-2 py-0.5 text-[10px] font-black tracking-wider text-indigo-600 uppercase dark:border-indigo-900/30 dark:bg-indigo-950/30 dark:text-indigo-400">
              {item.NoteType}
            </span>
          ) : (
            "-"
          );
        },
        className: "min-w-[100px] whitespace-nowrap",
      },
      {
        key: "CategoryName",
        header: "Category",
        alwaysShow: true,
        render: (row) => {
          const item = row.original;
          return (
            <span className="font-bold text-slate-900 dark:text-white" title={item.CategoryName}>
              {item.CategoryName}
            </span>
          );
        },
        className: "min-w-[160px] max-w-[200px] truncate",
      },
      {
        key: "ClientWish",
        header: "Client Wish",
        hasValue: (item) => {
          const raw = item.ClientWish || item.clientWish;
          if (!raw) return false;
          const val = stripHtml(raw);
          const hasMedia = /<(img|svg|iframe|video|audio|object|embed|canvas)[^>]*>/i.test(raw) || /&lt;(img|svg|iframe|video|audio|object|embed|canvas)[^&]*&gt;/i.test(raw);
          return (val && val !== "-") || hasMedia;
        },
        render: (row) => {
          const item = row.original;
          const raw = item.ClientWish || item.clientWish || "";
          const wishText = stripHtml(raw);
          const hasMedia = /<(img|svg|iframe|video|audio|object|embed|canvas)[^>]*>/i.test(raw) || /&lt;(img|svg|iframe|video|audio|object|embed|canvas)[^&]*&gt;/i.test(raw);
          const displayText = wishText || (hasMedia ? "[Image]" : "-");
          return (
            <span className="text-slate-650 dark:text-dark-100 max-w-[140px] truncate" title={wishText || (hasMedia ? "[Image]" : "")}>
              {displayText}
            </span>
          );
        },
        className: "min-w-[140px] max-w-[180px] truncate",
      },
      {
        key: "Cost",
        header: "Cost",
        hasValue: (item) => {
          const val = item.Cost;
          return val && String(val).trim() !== "" && String(val).trim() !== "-";
        },
        render: (row) => {
          const item = row.original;
          return (
            <span className="font-semibold text-slate-900 dark:text-white">
              {(() => {
                if (!item.Cost) return "-";
                let str = String(item.Cost).replace(/\$/g, "A$");
                if (/^[\d,.]/.test(str) && !str.includes("A$")) str = `A$${str}`;
                return str;
              })()}
            </span>
          );
        },
        className: "min-w-[100px] whitespace-nowrap",
      },
      {
        key: "AssignedToName",
        header: "Assign",
        hasValue: (item) => {
          const assignedUserName =
            item.AssignedToName ||
            item.AssignedTo ||
            users.find((u) => String(u.userId || u.UserId) === String(item.AssignedToUserId))?.fullName ||
            users.find((u) => String(u.userId || u.UserId) === String(item.AssignedToUserId))?.FullName;
          return assignedUserName && String(assignedUserName).trim() !== "" && String(assignedUserName).trim() !== "-";
        },
        render: (row) => {
          const item = row.original;
          const assignedUserName =
            item.AssignedToName ||
            item.AssignedTo ||
            users.find((u) => String(u.userId || u.UserId) === String(item.AssignedToUserId))?.fullName ||
            users.find((u) => String(u.userId || u.UserId) === String(item.AssignedToUserId))?.FullName ||
            "-";
          return (
            <span className="text-slate-650 dark:text-dark-100 max-w-[120px] truncate" title={assignedUserName}>
              {assignedUserName}
            </span>
          );
        },
        className: "min-w-[150px] max-w-[200px] truncate",
      },
      {
        key: "WorkStatusName",
        header: "Status",
        hasValue: (item) => {
          const workStatusName =
            item.WorkStatusName ||
            item.WorkStatus ||
            workStatuses.find((s) => String(s.Id) === String(item.WorkStatusId))?.StatusName;
          return workStatusName && String(workStatusName).trim() !== "" && String(workStatusName).trim() !== "-";
        },
        render: (row) => {
          const item = row.original;
          const workStatusName =
            item.WorkStatusName ||
            item.WorkStatus ||
            workStatuses.find((s) => String(s.Id) === String(item.WorkStatusId))
              ?.StatusName ||
            "-";
          return workStatusName === "-" ? (
            "-"
          ) : (
            <span
              className={`inline-block rounded px-2 py-0.5 text-[10px] font-bold ${getWorkStatusColor(workStatusName)}`}
            >
              {workStatusName}
            </span>
          );
        },
        className: "min-w-[120px] whitespace-nowrap",
      },
      {
        key: "CostStatusName",
        header: "Cost Status",
        hasValue: (item) => {
          const costStatusName =
            item.CostStatusName ||
            item.CostStatus ||
            costStatuses.find((s) => String(s.Id) === String(item.CostStatusId))?.StatusName;
          return costStatusName && String(costStatusName).trim() !== "" && String(costStatusName).trim() !== "-";
        },
        render: (row) => {
          const item = row.original;
          const costStatusName =
            item.CostStatusName ||
            item.CostStatus ||
            costStatuses.find((s) => String(s.Id) === String(item.CostStatusId))
              ?.StatusName ||
            "-";
          return costStatusName === "-" ? (
            "-"
          ) : (
            <span
              className={`inline-block rounded px-2 py-0.5 text-[10px] font-bold ${getCostStatusColor(costStatusName)}`}
            >
              {costStatusName}
            </span>
          );
        },
        className: "min-w-[140px] whitespace-nowrap",
      },
      {
        key: "CostAssignedToName",
        header: "Cost Status Assign",
        hasValue: (item) => {
          const costAssignedUserName =
            item.CostAssignedToName ||
            item.CostAssignedTo ||
            users.find((u) => String(u.userId || u.UserId) === String(item.CostAssignedToUserId))?.fullName ||
            users.find((u) => String(u.userId || u.UserId) === String(item.CostAssignedToUserId))?.FullName;
          return costAssignedUserName && String(costAssignedUserName).trim() !== "" && String(costAssignedUserName).trim() !== "-";
        },
        render: (row) => {
          const item = row.original;
          const costAssignedUserName =
            item.CostAssignedToName ||
            item.CostAssignedTo ||
            users.find((u) => String(u.userId || u.UserId) === String(item.CostAssignedToUserId))?.fullName ||
            users.find((u) => String(u.userId || u.UserId) === String(item.CostAssignedToUserId))?.FullName ||
            "-";
          return (
            <span className="text-slate-650 dark:text-dark-100 max-w-[120px] truncate" title={costAssignedUserName}>
              {costAssignedUserName}
            </span>
          );
        },
        className: "min-w-[160px] max-w-[220px] truncate",
      },
    ];

    const staticCols = allStaticCols.filter((col) => col.alwaysShow || hasData(col.hasValue));

    const modifiedAtCol = {
      key: "ModifiedAt",
      header: "Modified",
      meta: { isLast: true },
      render: (row) => {
        const item = row.original;
        const modDate = item.ModifiedAt || item.modifiedAt || item.ModifiedDate || item.modifiedDate;
        if (!modDate) return <span className="text-gray-400 dark:text-dark-500">—</span>;
        const modUser = item.ModifiedByName || resolveUserName(item.ModifiedBy || item.modifiedBy || item.ModifiedByUserId || item.modifiedByUserId);
        return (
          <div className="space-y-0.5 text-[11px]">
            <div className="font-semibold text-gray-700 dark:text-gray-300">
              {formatDisplayDate(modDate)}
            </div>
            <div className="dark:text-gray-550 text-[10px] font-medium text-gray-400">
              by {modUser}
            </div>
          </div>
        );
      },
      headerClassName: "w-[200px] min-w-[180px]",
      className: "min-w-[180px]",
    };

    const createdAtCol = {
      key: "CreatedAt",
      header: "Created",
      meta: { isLast: true },
      render: (row) => {
        const item = row.original;
        const createDate = item.CreatedAt || item.createdAt || item.CreatedDate || item.createdDate;
        const createUser = item.CreatedByName || resolveUserName(item.CreatedBy || item.createdBy || item.CreatedByUserId || item.createdByUserId);
        return (
          <div className="space-y-0.5 text-[11px]">
            <div className="font-semibold text-gray-700 dark:text-gray-300">
              {formatDisplayDate(createDate)}
            </div>
            <div className="dark:text-gray-550 text-[10px] font-medium text-gray-400">
              by {createUser}
            </div>
          </div>
        );
      },
      headerClassName: "w-[200px] min-w-[180px]",
      className: "min-w-[180px]",
    };

    const dynamicCols = (availableColumns || [])
      .filter((col) => {
        const fieldName = col.FieldName || col.fieldName || col.ColumnKey;
        return hasData((row) => {
          const val = getDynamicFieldValue({ original: row }, fieldName);
          if (val === null || val === undefined) return false;
          const str = String(val).trim();
          return str !== "" && str !== "-" && str !== "[]" && str !== "null" && str !== "undefined";
        });
      })
      .map((col) => ({
      key: col.ColumnKey,
      header: col.DisplayName,
      render: (row) => <DynamicCellRenderer row={row} fieldName={col.FieldName || col.fieldName || col.ColumnKey} onPreviewDocument={onPreviewDocument} />,
      sortable: false,
    }));

    const actionsCol = {
      key: "actions",
      header: "Actions",
      sortable: false,
      render: (row) => {
        const item = row.original;
        return renderActionsMenu(item);
      },
      headerClassName: "text-right min-w-[100px]",
      className: "text-right whitespace-nowrap min-w-[100px]",
    };

    const dateCols = [];
    if (hasData((row) => row.CreatedAt || row.createdAt)) dateCols.push(createdAtCol);
    if (hasData((row) => row.ModifiedAt || row.modifiedAt)) dateCols.push(modifiedAtCol);

    return [...staticCols, ...dynamicCols, ...dateCols, actionsCol];
  }, [users, workStatuses, costStatuses, onView, onEdit, onDelete, getWorkStatusColor, getCostStatusColor, availableColumns, data, resolveUserName]);

  const renderCardView = () => {
    if (paginatedData.length === 0) {
      return (
        <div className="dark:bg-dark-800 border-gray-150 dark:border-dark-700/50 flex flex-col items-center justify-center rounded-2xl border bg-white px-4 py-16 text-center">
          <svg
            className="text-slate-350 dark:text-dark-600 mb-3 h-10 w-10"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth="1.5"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M20.25 7.5l-.625 10.632a2.25 2.25 0 01-2.247 2.118H6.622a2.25 2.25 0 01-2.247-2.118L3.75 7.5m6 4.125l2.25 2.25m0 0l2.25 2.25m-2.25-2.25l-2.25 2.25m2.25-2.25l2.25-2.25M3.75 5.625c0-.621.504-1.125 1.125-1.125h14.25c.621 0 1.125.504 1.125 1.125v1.875c0 .621-.504 1.125-1.125 1.125H4.875C4.254 8.625 3.75 8.121 3.75 7.5V5.625z"
            />
          </svg>
          <h3 className="dark:text-dark-100 text-sm font-bold text-slate-800">
            {search ? "No search results" : "No Client Notes"}
          </h3>
          <p className="dark:text-dark-400 mt-1 max-w-xs text-xs leading-relaxed text-slate-500">
            {search
              ? `We couldn't find any notes matching "${search}".`
              : "Create your first client note to track specifications for this lead opportunity."}
          </p>
        </div>
      );
    }

    return (
      <div
        className={`animate-fade-in grid gap-4 ${isMobile ? "sm:grid-cols-1 md:grid-cols-1 lg:grid-cols-2" : "grid-cols-1 md:grid-cols-2 xl:grid-cols-3"}`}
      >
        {" "}
        {paginatedData.map((row) => {
          const workStatusName =
            row.WorkStatusName ||
            row.WorkStatus ||
            workStatuses.find((s) => String(s.Id) === String(row.WorkStatusId))
              ?.StatusName ||
            "-";
          const costStatusName =
            row.CostStatusName ||
            row.CostStatus ||
            costStatuses.find((s) => String(s.Id) === String(row.CostStatusId))
              ?.StatusName ||
            "-";
          const assignedUserName =
            row.AssignedToName ||
            row.AssignedTo ||
            users.find((u) => String(u.userId || u.UserId) === String(row.AssignedToUserId))?.fullName ||
            users.find((u) => String(u.userId || u.UserId) === String(row.AssignedToUserId))?.FullName ||
            "-";
          const costAssignedUserName =
            row.CostAssignedToName ||
            row.CostAssignedTo ||
            users.find((u) => String(u.userId || u.UserId) === String(row.CostAssignedToUserId))?.fullName ||
            users.find((u) => String(u.userId || u.UserId) === String(row.CostAssignedToUserId))?.FullName ||
            "-";

          const wishText = stripHtml(row.ClientWish || row.clientWish);

          const createdAt = row.CreatedAt || row.createdAt || row.CreatedDate || row.createdDate;
          const createdUserName =
            row.CreatedByName ||
            resolveUserName(row.CreatedBy || row.createdBy || row.CreatedByUserId || row.createdByUserId);

          const modifiedAt = row.ModifiedAt || row.modifiedAt || row.ModifiedDate || row.modifiedDate;
          const modifiedUserName =
            row.ModifiedByName ||
            resolveUserName(row.ModifiedBy || row.modifiedBy || row.ModifiedByUserId || row.modifiedByUserId);

          return (
            <div
              key={row.Id}
              className="dark:bg-dark-800 border-gray-150 dark:border-dark-700/50 flex flex-col justify-between rounded-2xl border bg-white p-4 shadow-[0_2px_8px_rgba(0,0,0,0.02)] transition-all duration-200 hover:-translate-y-0.5 hover:shadow-[0_4px_12px_rgba(0,0,0,0.05)]"
            >
              {/* Header: Category + Actions */}
              <div className="dark:border-dark-700/40 mb-3 flex items-start justify-between gap-2 border-b border-gray-100 pb-2.5">
                <div className="min-w-0">
                  <h3
                    className="truncate text-sm font-bold text-slate-900 dark:text-white"
                    title={row.CategoryName}
                  >
                    {row.CategoryName}
                  </h3>
                  <div className="mt-1 flex flex-wrap gap-1.5">
                    {wishText && (
                      <span className="dark:text-dark-300 dark:bg-dark-850 dark:border-dark-750 inline-block rounded-md border border-slate-100 bg-slate-50 px-2 py-0.5 text-[10px] font-semibold text-slate-600 truncate max-w-[160px]" title={wishText}>
                        {wishText}
                      </span>
                    )}
                    {row.NoteType && (
                      <span className="inline-block rounded-md border border-indigo-100/80 bg-indigo-50 px-2 py-0.5 text-[10px] font-black tracking-wider text-indigo-600 uppercase dark:border-indigo-900/30 dark:bg-indigo-950/30 dark:text-indigo-400">
                        {row.NoteType}
                      </span>
                    )}
                  </div>
                </div>

                <div className="flex shrink-0 items-center justify-end">
                  {renderActionsMenu(row)}
                </div>
              </div>

              {/* Details Grid */}
              <div className="mb-3.5 grid grid-cols-2 gap-x-4 gap-y-3 text-xs">
                <div>
                  <span className="dark:text-dark-500 mb-0.5 block text-[10px] font-bold tracking-wider text-slate-400 uppercase">
                    Cost
                  </span>
                  <span className="font-bold text-slate-900 dark:text-white">
                    {(() => {
                      if (!row.Cost) return "-";
                      let str = String(row.Cost).replace(/\$/g, "A$");
                      if (/^[\d,.]/.test(str) && !str.includes("A$")) str = `A$${str}`;
                      return str;
                    })()}
                  </span>
                </div>
                <div>
                  <span className="dark:text-dark-500 mb-0.5 block text-[10px] font-bold tracking-wider text-slate-400 uppercase">
                    Assign
                  </span>
                  <span
                    className="dark:text-dark-100 block truncate font-semibold text-slate-800"
                    title={assignedUserName}
                  >
                    {assignedUserName}
                  </span>
                </div>
                <div>
                  <span className="dark:text-dark-500 mb-0.5 block text-[10px] font-bold tracking-wider text-slate-400 uppercase">
                    Status
                  </span>
                  {workStatusName === "-" ? (
                    <span className="dark:text-dark-100 font-semibold text-slate-800">
                      -
                    </span>
                  ) : (
                    <span
                      className={`inline-block rounded px-2 py-0.5 text-[10px] font-bold ${getWorkStatusColor(workStatusName)}`}
                    >
                      {workStatusName}
                    </span>
                  )}
                </div>
                <div>
                  <span className="dark:text-dark-500 mb-0.5 block text-[10px] font-bold tracking-wider text-slate-400 uppercase">
                    Cost Status
                  </span>
                  {costStatusName === "-" ? (
                    <span className="dark:text-dark-100 font-semibold text-slate-800">
                      -
                    </span>
                  ) : (
                    <span
                      className={`inline-block rounded px-2 py-0.5 text-[10px] font-bold ${getCostStatusColor(costStatusName)}`}
                    >
                      {costStatusName}
                    </span>
                  )}
                </div>
                <div className="col-span-2">
                  <span className="dark:text-dark-500 mb-0.5 block text-[10px] font-bold tracking-wider text-slate-400 uppercase">
                    Cost Status Assign
                  </span>
                  <span
                    className="dark:text-dark-100 block truncate font-semibold text-slate-800"
                    title={costAssignedUserName}
                  >
                    {costAssignedUserName}
                  </span>
                </div>
              </div>

              {/* Footer metadata */}
              <div className="dark:border-dark-700/40 space-y-2 border-t border-gray-100 pt-2.5 text-[11px] font-semibold text-slate-400 dark:text-dark-500">
                {/* Created */}
                <div className="space-y-0.5">
                  <span className="block text-[10px] font-bold uppercase tracking-wider text-slate-400 dark:text-dark-500">
                    Created
                  </span>
                  <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                    <div className="flex items-center gap-1 font-semibold text-slate-700 dark:text-gray-300">
                      <ClockIcon className="h-3.5 w-3.5 shrink-0 opacity-65" />
                      <span>{createdAt ? formatDisplayDate(createdAt) : "—"}</span>
                    </div>
                    <div className="flex items-center gap-1 text-[10.5px] text-slate-500 dark:text-gray-400">
                      <UserIcon className="h-3.5 w-3.5 shrink-0 opacity-65" />
                      <span>by {createdUserName}</span>
                    </div>
                  </div>
                </div>

                {/* Modified */}
                <div className="space-y-0.5">
                  <span className="block text-[10px] font-bold uppercase tracking-wider text-slate-400 dark:text-dark-500">
                    Modified
                  </span>
                  {modifiedAt ? (
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                      <div className="flex items-center gap-1 font-semibold text-slate-700 dark:text-gray-300">
                        <ClockIcon className="h-3.5 w-3.5 shrink-0 opacity-65" />
                        <span>{formatDisplayDate(modifiedAt)}</span>
                      </div>
                      <div className="flex items-center gap-1 text-[10.5px] text-slate-500 dark:text-gray-400">
                        <UserIcon className="h-3.5 w-3.5 shrink-0 opacity-65" />
                        <span>by {modifiedUserName}</span>
                      </div>
                    </div>
                  ) : (
                    <div className="text-gray-400 dark:text-dark-500">—</div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const renderTableView = () => {
    return (
      <div className="flex-1 h-full min-h-[400px] flex flex-col min-w-0">
        <SalesPageLayoutContext.Provider value={portalElement ? { rightContainer: portalElement } : null}>
          <CustomTable
            columns={columns}
            data={paginatedData}
            storageKey="client-notes-table-v4"
            persistSettings={true}
            showToolbar={true}
            showPagination={true}
            loading={loading}
            pagination={{ page: currentPage, limit: itemsPerPage, total: totalRecords }}
            onPageChange={setCurrentPage}
            onLimitChange={(limit) => {
              if (setPageSize) setPageSize(limit);
              if (setCurrentPage) setCurrentPage(1);
            }}
            sortBy={sortConfig.key}
            sortOrder={sortConfig.direction}
            onSort={(key, direction) => setSortConfig({ key, direction })}
            onRowClick={onView}
            noDataMessage={search ? "No search results" : "No Client Notes"}
            noDataSubtitle={
              search
                ? `We couldn't find any notes matching "${search}".`
                : "Create your first client note to track specifications for this lead opportunity."
            }
            showColumnConfig={true}
            showSearch={false}
            showStatusFilter={false}
            showActionButtons={false}
            title=""
            headerActions={null}
            columnVisibility={visibility}
            onColumnVisibilityChange={setVisibility}
          />
        </SalesPageLayoutContext.Provider>
      </div>
    );
  };

  return (
    <div className="flex h-full min-h-0 flex-1 flex-col space-y-4">
      {/* Custom Toolbar Header */}
      {/* Custom Toolbar Header */}
      {isMobile ? (
        <div className="border-gray-150 dark:border-dark-700/50 flex flex-col gap-3 border-b pb-2">
          <div className="flex w-full items-center justify-between">
            <h2 className="text-[16px] font-bold text-gray-900 dark:text-white">
              Client Notes ({data.length})
            </h2>
            <div className="flex items-center gap-2">
              {/* Table Column Config Portal Target */}
              {viewMode === "table" && (
                <div ref={portalRef} className="flex items-center shrink-0" />
              )}
              {/* View Mode Switcher */}
              <div className="dark:border-dark-700/80 dark:bg-dark-850 flex shrink-0 items-center rounded-lg border border-gray-200 bg-gray-50 p-0.5">
                <button
                  type="button"
                  onClick={() => setViewMode("table")}
                  className={`rounded-md p-1 transition-all ${viewMode === "table"
                    ? "dark:bg-dark-700 text-primary-650 dark:text-primary-400 bg-white shadow-xs"
                    : "hover:text-gray-650 dark:hover:text-dark-200 text-gray-400"
                    }`}
                  title="List View"
                >
                  <ListBulletIcon className="h-3.5 w-3.5" />
                </button>
                <button
                  type="button"
                  onClick={() => setViewMode("card")}
                  className={`rounded-md p-1 transition-all ${viewMode === "card"
                    ? "dark:bg-dark-700 text-primary-650 dark:text-primary-400 bg-white shadow-xs"
                    : "hover:text-gray-650 dark:hover:text-dark-200 text-gray-400"
                    }`}
                  title="Card View"
                >
                  <Squares2X2Icon className="h-3.5 w-3.5" />
                </button>
              </div>
              {canEdit && (
                <Button
                  onClick={onAddClick}
                  color="primary"
                  className="animate-fade-in flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-bold shadow-xs"
                >
                  <PlusIcon className="h-3.5 w-3.5" />
                  <span>Add</span>
                </Button>
              )}
            </div>
          </div>

          {/* Custom Search & Filter - Mobile */}
          <div className="flex w-full gap-2 items-center">
            <div className="flex-1">
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search notes..."
                prefix={<MagnifyingGlassIcon className="h-4 w-4 text-gray-400" />}
                suffix={
                  search ? (
                    <button
                      onClick={() => setSearch("")}
                      className="dark:hover:bg-dark-700 text-gray-450 hover:text-gray-650 rounded-full p-0.5 transition-colors hover:bg-gray-100"
                    >
                      <XMarkIcon className="h-3.5 w-3.5" />
                    </button>
                  ) : null
                }
                classNames={{
                  input: "h-[34px] w-full text-xs",
                }}
              />
            </div>
            {noteTypeOptions && noteTypeOptions.length > 0 && (
              <div className="flex items-center gap-1 shrink-0">
                <FunnelIcon className="h-4 w-4 text-gray-400 dark:text-dark-300" />:
                <div className="w-[140px] sm:w-[160px]">
                  <MultiSelectCheckboxDropdown
                    options={noteTypeOptions}
                    value={selectedNoteTypes}
                    onChange={(val) => {
                      if (setSelectedNoteTypes) {
                        setSelectedNoteTypes(val);
                        setCurrentPage(1);
                      }
                    }}
                    placeholder="Type"
                    isMulti={true}
                    usePortal={true}
                    className="[&_.trigger-wrapper]:h-[34px] [&_.trigger-wrapper_.text-sm]:text-xs [&_.trigger-wrapper_.h-6]:h-5"
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="border-gray-150 dark:border-dark-700/50 flex flex-row items-center justify-between gap-2 border-b pb-2 flex-nowrap overflow-x-auto hide-scrollbar">
          <div className="flex items-center gap-2 shrink-0">
            <h2 className="text-sm font-bold whitespace-nowrap text-gray-900 dark:text-white">
              Client Notes ({data.length})
            </h2>

            {/* View Mode Switcher */}
            <div className="dark:border-dark-700/80 dark:bg-dark-850 flex shrink-0 items-center rounded-lg border border-gray-200 bg-gray-50 p-0.5">
              <button
                type="button"
                onClick={() => setViewMode("table")}
                className={`rounded-md p-1.5 transition-colors ${viewMode === "table"
                  ? "dark:bg-dark-700 text-primary-650 dark:text-primary-400 bg-white shadow-xs"
                  : "hover:text-gray-650 dark:hover:text-dark-200 text-gray-400"
                  }`}
                title="List View"
              >
                <ListBulletIcon className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={() => setViewMode("card")}
                className={`rounded-md p-1.5 transition-colors ${viewMode === "card"
                  ? "dark:bg-dark-700 text-primary-650 dark:text-primary-400 bg-white shadow-xs"
                  : "hover:text-gray-650 dark:hover:text-dark-200 text-gray-400"
                  }`}
                title="Card View"
              >
                <Squares2X2Icon className="h-4 w-4" />
              </button>
            </div>
                        {/* Table Column Config Portal Target */}
            {viewMode === "table" && (
              <div ref={portalRef} className="flex items-center shrink-0" />
            )}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {/* Custom Search Input */}
            {/* Note Type Filter */}
            {noteTypeOptions && noteTypeOptions.length > 0 && (
              <div className="flex items-center gap-1.5 shrink-0">
                <FunnelIcon className="h-4 w-4 text-gray-400 dark:text-dark-300" />:
                <MultiSelectCheckboxDropdown
                  options={noteTypeOptions}
                  value={selectedNoteTypes}
                  onChange={(val) => {
                    if (setSelectedNoteTypes) {
                      setSelectedNoteTypes(val);
                      setCurrentPage(1);
                    }
                  }}
                  placeholder="Filter by type"
                  className="w-[140px] lg:w-[171px] shrink [&_.trigger-wrapper]:h-9"
                  isMulti={true}
                  usePortal={true}
                />
              </div>
            )}
             <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search category..."
              prefix={<MagnifyingGlassIcon className="h-4 w-4 text-gray-400" />}
              suffix={
                search ? (
                  <button
                    onClick={() => setSearch("")}
                    className="dark:hover:bg-dark-700 text-gray-450 hover:text-gray-650 rounded-full p-0.5 transition-colors hover:bg-gray-100"
                  >
                    <XMarkIcon className="h-3.5 w-3.5" />
                  </button>
                ) : null
              }
              classNames={{
                input: "h-[36px] w-[130px] lg:w-[171px] text-xs shrink min-w-0",
              }}
            />
            {canEdit && (
              <Button
                onClick={onAddClick}
                color="primary"
                className="animate-fade-in flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-bold shadow-xs"
              >
                <PlusIcon className="h-3.5 w-3.5" />
                <span>Add</span>
              </Button>
            )}
          </div>
        </div>
      )}

      {/* New Custom Table/Card Layout */}
      <div className="flex min-h-0 flex-1 flex-col justify-between">
        {viewMode === "card" ? (
          loading && (!data || data.length === 0) ? (
            <div className="dark:bg-dark-800 border-gray-150 dark:border-dark-700/50 flex flex-col items-center justify-center rounded-2xl border bg-white py-20">
              <div className="border-primary-500 h-8 w-8 animate-spin rounded-full border-4 border-t-transparent"></div>
              <p className="mt-3 text-xs font-semibold text-gray-500">
                Loading client notes...
              </p>
            </div>
          ) : (
            <div className="relative min-h-[200px] flex flex-col flex-1">
              {loading && data && data.length > 0 && (
                <div className="absolute inset-0 z-50 flex items-center justify-center bg-white/50 dark:bg-dark-900/50 backdrop-blur-[1px] rounded-2xl">
                  <div className="h-8 w-8 rounded-full border-4 border-primary-500 border-t-transparent animate-spin"></div>
                </div>
              )}
              {renderCardView()}

              {/* Custom Pagination Section */}
              {totalPages > 1 && (
                <div
                  className={`flex ${isMobile ? "flex-col items-center gap-3" : "items-center justify-between"} border-gray-150 dark:border-dark-700/50 mt-2 border-t px-1 pt-4`}
                >
                  <span className="dark:text-dark-400 text-[11px] font-semibold text-slate-400">
                    {isMobile
                      ? `Page ${currentPage} of ${totalPages} (${totalRecords} entries)`
                      : `Showing ${(currentPage - 1) * itemsPerPage + 1} to ${Math.min(currentPage * itemsPerPage, totalRecords)} of ${totalRecords} entries`}
                  </span>
                  <div className="flex items-center gap-1">
                    <Button
                      onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                      variant="outlined"
                      color="neutral"
                      className="cursor-pointer rounded-lg px-2.5 py-1 text-[10px] font-bold shadow-xs"
                    >
                      Previous
                    </Button>
                    {!isMobile && (
                      <div className="flex items-center gap-1 px-2">
                        {Array.from({ length: totalPages }).map((_, i) => (
                          <button
                            key={i + 1}
                            onClick={() => setCurrentPage(i + 1)}
                            className={`flex h-6 w-6 items-center justify-center rounded-lg text-[10px] font-black transition-colors ${currentPage === i + 1
                              ? "bg-primary text-white"
                              : "dark:hover:bg-dark-750 text-gray-500 hover:bg-gray-100"
                              }`}
                          >
                            {i + 1}
                          </button>
                        ))}
                      </div>
                    )}
                    <Button
                      onClick={() =>
                        setCurrentPage((p) => Math.min(totalPages, p + 1))
                      }
                      disabled={currentPage === totalPages}
                      variant="outlined"
                      color="neutral"
                      className="cursor-pointer rounded-lg px-2.5 py-1 text-[10px] font-bold shadow-xs"
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )
        ) : (
          renderTableView()
        )}
      </div>
    </div>
  );
}
